import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Input, MessageBarBody, MessageBarTitle } from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';

// models
import {
  CreatePersonAddressCommand,
  UpdatePersonAddressCommand
} from '../../../models/Entities/Person/Address/AddressModels';

// components
import { ErrorMessageComponent } from '../../Error/ErrorMessageComponent';
import { GuideDrawer, GuideDrawerForm } from '../../../theme/components/GuideDrawer';
import { GuideField, GuideFieldGrid } from '../../../theme/components/GuideField';
import { GuideMessageBar } from '../../../theme/components/GuideMessageBar';

// functions
import * as ownFunctions from './PersonAddress.Form.Functions';

interface FormValidation {
  success: boolean;
  errors: string[];
}

const PersonAddressFormComponent = (props: ownFunctions.Props) => {
  const [t] = useTranslation();

  const [submitted, setSubmitted] = useState<boolean>(false);
  const [validation, setValidation] = useState<FormValidation | undefined>();

  const [street, setStreet] = useState<string>('');
  const [city, setCity] = useState<string>('');
  const [postalCode, setPostalCode] = useState<string>('');
  const [country, setCountry] = useState<string>('');

  useEffect(() => {
    if (props.address) {
      setStreet(props.address.street || '');
      setCity(props.address.city || '');
      setPostalCode(props.address.postalCode || '');
      setCountry(props.address.country || '');
    } else {
      resetForm();
    }
  }, [props.address, props.isOpen]);

  useEffect(() => {
    if (submitted && (props.successAdding || props.successUpdating)) {
      resetForm();
      props.dismissPanel();
    }
  }, [props.successAdding, props.successUpdating]);

  const resetForm = () => {
    setStreet('');
    setCity('');
    setPostalCode('');
    setCountry('');
    setSubmitted(false);
    setValidation(undefined);
  };

  const validate = (): boolean => {
    const errors: string[] = [];

    if (!street.trim()) {
      errors.push(t('persons.addresses.form.validation.street', { defaultValue: 'La calle es obligatoria' }));
    }

    if (!city.trim()) {
      errors.push(t('persons.addresses.form.validation.city', { defaultValue: 'La ciudad es obligatoria' }));
    }

    const result: FormValidation = { success: errors.length === 0, errors };
    setValidation(result);
    return result.success;
  };

  const handleSave = () => {
    setSubmitted(true);
    if (!validate()) return;

    const payload: CreatePersonAddressCommand = {
      personId: props.PersonId,
      street: street.trim(),
      city: city.trim(),
      postalCode: postalCode.trim() || null,
      country: country.trim() || null
    };

    if (props.address) {
      props.UpdateAddress({ ...payload, id: props.address.id } as UpdatePersonAddressCommand);
    } else {
      props.CreateAddress(payload);
    }
  };

  const handleCancel = () => {
    props.dismissPanel();
    resetForm();
  };

  const isSaving = !!(props.isAdding || props.isUpdating);

  return (
    <GuideDrawer
      open={props.isOpen}
      size="medium"
      eyebrow={props.personName || undefined}
      title={
        props.address
          ? t('persons.addresses.form.editTitle', { defaultValue: 'Editar domicilio' })
          : t('persons.addresses.form.addTitle', { defaultValue: 'Nuevo domicilio' })
      }
      onDismiss={handleCancel}
      preventDismiss={isSaving}
      testId="person-address-drawer"
      secondaryAction={{ label: t('persons.form.footer.cancel', { defaultValue: 'Cancelar' }) }}
      primaryAction={{
        label: t('persons.form.footer.save', { defaultValue: 'Guardar' }),
        onClick: handleSave,
        loading: isSaving
      }}
    >
      {submitted && validation && !validation.success && (
        <GuideMessageBar intent="error" layout="multiline">
          <MessageBarBody>
            <MessageBarTitle>{t('persons.form.review', { defaultValue: 'Revise la información ingresada' })}</MessageBarTitle>
            <ul>
              {validation.errors.map((error: string, index: number) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </MessageBarBody>
        </GuideMessageBar>
      )}

      {submitted && (props.failAdding || props.failUpdating) && props.error && (
        <ErrorMessageComponent Error={props.error} />
      )}

      <GuideDrawerForm onSubmit={handleSave}>
        <GuideField
          label={t('persons.addresses.form.street', { defaultValue: 'Calle y número' })}
          required
          error={
            submitted && !street.trim()
              ? t('persons.addresses.form.validation.street', { defaultValue: 'La calle es obligatoria' })
              : undefined
          }
        >
          <Input value={street} onChange={(_, data) => setStreet(data.value || '')} />
        </GuideField>

        <GuideFieldGrid>
          <GuideField
            label={t('persons.addresses.form.city', { defaultValue: 'Ciudad' })}
            required
            error={
              submitted && !city.trim()
                ? t('persons.addresses.form.validation.city', { defaultValue: 'La ciudad es obligatoria' })
                : undefined
            }
          >
            <Input value={city} onChange={(_, data) => setCity(data.value || '')} />
          </GuideField>

          <GuideField label={t('persons.addresses.form.postalCode', { defaultValue: 'Código postal' })}>
            <Input value={postalCode} onChange={(_, data) => setPostalCode(data.value || '')} />
          </GuideField>
        </GuideFieldGrid>

        <GuideField label={t('persons.addresses.form.country', { defaultValue: 'País' })}>
          <Input value={country} onChange={(_, data) => setCountry(data.value || '')} />
        </GuideField>
      </GuideDrawerForm>
    </GuideDrawer>
  );
};

export default connect(
  ownFunctions.mapStateToProps,
  ownFunctions.mapDispatchToProps
)(PersonAddressFormComponent as any);
